/**
 * PaneFooter — THE bottom edge of a fixed-height pane: a stat on the left,
 * the pane's action on the right.
 *
 * The shape was invented inline at the bottom of `AccessPane` — a count badge
 * ("4 people") and one button ("Manage access") — and it is the pattern every
 * pane now follows, because it answers the two questions a docked pane is read
 * for in the two places the eye lands last: how much is in here, and what can
 * I do about it.
 *
 * WHERE IT APPLIES.
 *
 *   - Fixed-height PANES (the docked glass boxes): footer. The pane owns its
 *     height, so a footer is always on screen and never has to be scrolled to.
 *   - Scrolling CANVAS cards: action stays in the `GlassCard` header. A footer
 *     under a list that grows is a footer nobody reaches.
 *   - Context panes are EXEMPT: they are facts to read, with no single action
 *     that belongs to the whole pane.
 *
 * The stat is a fact about the pane's own content, not a status: "12 events",
 * "4 people", "3 of 7 complete". Pass `count` + `label` for the standard badge
 * rendering, or `stat` for anything else.
 */

import React from 'react';
import clsx from 'clsx';
import Badge from '../TailwindComponents/Badge';

export interface PaneFooterProps {
  /** The standard stat: a count badge followed by an already-translated noun. */
  count?: number | string;
  /** Already-translated label after the badge ("people", "events", "resources"). */
  label?: React.ReactNode;
  /** Anything other than count + label — a progress tally, a muted note. */
  stat?: React.ReactNode;
  /** The pane's action. Right-aligned, and normally a single small button. */
  children?: React.ReactNode;
  className?: string;
}

const PaneFooter: React.FC<PaneFooterProps> = ({
  count,
  label,
  stat,
  children,
  className,
}) => (
  <div
    className={clsx(
      'flex-shrink-0 flex items-center justify-between gap-2',
      'px-3 py-2 border-t border-hairline-soft',
      className
    )}
    data-testid="pane-footer"
  >
    <span className="flex min-w-0 items-center gap-1.5 text-[11px] text-fg-muted">
      {stat ?? (
        count !== undefined && (
          <>
            <Badge color="gray">{count}</Badge>
            {label}
          </>
        )
      )}
    </span>
    {/* Always rendered, so a pane with no action still puts its stat on the
        same baseline as the pane stacked above it. */}
    <span className="flex flex-shrink-0 items-center gap-2">{children}</span>
  </div>
);

export default PaneFooter;
